/**
 * COMPTABILITÉ — BANQUE & DÉPÔTS.
 *
 * ═══ CE QUE CET ÉCRAN SUIT ═══
 *
 *     À PRÉPARER  →  PRÉPARÉ  →  DÉPOSÉ  →  RAPPROCHÉ
 *
 * Une clôture produit des espèces ; quelqu'un doit pouvoir répondre à « qu'est-ce
 * qui est parti, quand, et est-ce que le compte y est ? ». Une remise regroupe
 * PLUSIEURS nuits — c'est le cas courant, on ne va pas à la banque après chaque
 * soirée — et son détail montre donc les nuits qu'elle contient.
 *
 * ═══ TROIS MONTANTS, ENCORE ═══
 *
 *  · attendu  — la somme des nuits rattachées, CALCULÉE par le serveur ;
 *  · préparé  — ce qui a réellement été compté et mis dans la sacoche ;
 *  · déposé   — ce que le relevé bancaire confirme.
 *
 * L'écart entre l'attendu et le déposé est VISIBLE, et l'accepter exige une
 * explication d'au moins douze caractères utiles. Le motif est demandé ICI
 * avant l'envoi, mais c'est le CŒUR qui tranche : il compare l'attendu au
 * crédité et refuse avec une phrase qui chiffre l'écart. L'écran ne prétend
 * jamais avoir enregistré ce que le serveur n'a pas accepté.
 *
 * ═══ DEUX MONTANTS D'ESPÈCES À NE PAS CONFONDRE ═══
 *
 * Une nuit en attente porte `counted_cents` (l'argent PHYSIQUE mesuré) et
 * `recognised_cents` (la recette espèces RECONNUE par la clôture). Ils peuvent
 * légitimement différer — fonds de caisse, appoint — et les confondre ferait
 * apparaître un écart de rapprochement qui n'existe pas. Ce qu'on met en avant
 * est le RESTE À DÉPOSER, seul chiffre actionnable.
 *
 * ═══ CE QUE CET ÉCRAN NE FAIT PAS ═══
 *
 * Il ne se connecte à AUCUNE banque. Aucun agrégateur, aucun identifiant
 * bancaire, aucun IBAN complet : le rapprochement est une saisie humaine
 * confrontée à un relevé lu ailleurs. Aucun tiroir latéral non plus : le détail
 * d'une remise remplace la liste dans la page, et l'on y revient d'un bouton.
 *
 * ROUTES SERVIES (`apps/api/src/routes/finance-revenue.ts`) :
 *   GET  /api/v1/finance/deposits/cash-to-deposit      finance.bank.read
 *   GET  /api/v1/finance/deposits                      finance.bank.read
 *   POST /api/v1/finance/deposits                      finance.deposit.prepare
 *   POST /api/v1/finance/deposits/:id/prepare          finance.deposit.prepare
 *   POST /api/v1/finance/deposits/:id/confirm          finance.deposit.confirm
 *   POST /api/v1/finance/deposits/:id/reconcile        finance.deposit.reconcile (step-up)
 *   GET  /api/v1/finance/references/bank-accounts      finance.bank.read
 */

import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Banknote, Landmark, Plus } from 'lucide-react'
import { Link } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, Select, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { DatePicker, SegmentedControl } from '../admin/pickers.js'
import { Checkbox, Field, TextInput } from '../admin/form.js'
import { useConfirm } from '../shell/confirm.js'
import { formatMoney, parseMoney } from './money.js'
import { remplacerRecherche } from './adresse-ecran.js'
import {
  Ecart, EtatBloc, MotifChamp,
  cents, entier, estObjet, libelleJour, libelleJourLong, motifValide, texte,
  totalChamp, useRessource, ymdAujourdhui,
} from './RevenueShared.js'
import './finance.css'
import './finance-revenue.css'
import './finance-bank.css'

/* ═══════════════════════════ le vocabulaire ═══════════════════════════ */

type StatutRemise = 'A_PREPARER' | 'PREPARE' | 'DEPOSE' | 'RAPPROCHE'

const ETAPES: { code: StatutRemise; label: string }[] = [
  { code: 'A_PREPARER', label: 'À préparer' },
  { code: 'PREPARE', label: 'Préparée' },
  { code: 'DEPOSE', label: 'Déposée' },
  { code: 'RAPPROCHE', label: 'Rapprochée' },
]

function rangEtape(s: StatutRemise): number { return ETAPES.findIndex((e) => e.code === s) }
function libelleEtape(s: StatutRemise): string { return ETAPES[rangEtape(s)]?.label ?? s }

/** Une nuit finalisée dont il reste des espèces à porter en banque. */
interface NuitEspeces {
  closingId: string
  date: string
  revision: number | null
  /** L'argent PHYSIQUE compté. */
  compte: number | null
  /** La recette espèces RECONNUE par la clôture. */
  reconnu: number | null
  /** Ce qui reste à déposer — le seul chiffre actionnable. */
  reste: number | null
}

interface Remise {
  id: string
  reference: string | null
  statut: StatutRemise
  compte: string | null
  attendu: number | null
  prepare: number | null
  depose: number | null
  dateDepot: string | null
  ecart: number | null
  motifEcart: string | null
  notes: string | null
  nuits: { closingId: string | null; date: string; cents: number | null }[]
  tracabilite: { label: string; valeur: string }[]
}

function nuitEspecesDe(v: unknown): NuitEspeces | null {
  if (!estObjet(v)) return null
  const date = texte(v['business_date'])
  const closingId = texte(v['closing_id'])
  if (date === null || closingId === null) return null
  return {
    closingId, date,
    revision: entier(v['revision']),
    compte: cents(v['counted_cents']),
    reconnu: cents(v['recognised_cents']),
    reste: cents(v['remaining_cents']),
  }
}

function statutRemiseDe(v: unknown): StatutRemise {
  const t = texte(v)
  return ETAPES.some((e) => e.code === t) ? (t as StatutRemise) : 'A_PREPARER'
}

function trace(v: Record<string, unknown>, qui: string, quand: string, label: string): { label: string; valeur: string }[] {
  const nom = texte(v[qui])
  const date = texte(v[quand])
  if (nom === null && date === null) return []
  return [{ label, valeur: `${nom ?? 'Auteur inconnu'}${date !== null ? ` · ${libelleJour(date.slice(0, 10))}` : ''}` }]
}

function remiseDe(v: unknown): Remise | null {
  if (!estObjet(v)) return null
  const id = texte(v['id'])
  if (id === null) return null
  const attendu = cents(v['expected_cents'])
  const depose = cents(v['deposited_cents'])
  return {
    id,
    reference: texte(v['reference']),
    statut: statutRemiseDe(v['status']),
    compte: texte(v['bank_account_name']),
    attendu,
    prepare: cents(v['prepared_cents']),
    depose,
    dateDepot: texte(v['deposit_date']),
    // L'API ne transmet pas d'écart : il se DÉDUIT de deux montants réellement
    // lus, et reste `null` tant que l'un des deux manque — jamais zéro.
    ecart: attendu !== null && depose !== null ? depose - attendu : null,
    motifEcart: texte(v['difference_reason']),
    notes: texte(v['notes']),
    nuits: Array.isArray(v['items'])
      ? v['items'].flatMap((it) => {
        if (!estObjet(it)) return []
        const date = texte(it['business_date'])
        return date === null ? [] : [{ closingId: texte(it['closing_id']), date, cents: cents(it['amount_cents']) }]
      })
      : [],
    tracabilite: [
      ...trace(v, 'prepared_by_name', 'prepared_at', 'Préparée par'),
      ...trace(v, 'deposited_by_name', 'deposited_at', 'Déposée par'),
      ...trace(v, 'reconciled_by_name', 'reconciled_at', 'Rapprochée par'),
    ],
  }
}

interface EnAttente { nuits: NuitEspeces[]; total: number | null }

function lireEnAttente(v: unknown): EnAttente | null {
  if (!estObjet(v) || !Array.isArray(v['nights'])) return null
  const nuits = v['nights'].map(nuitEspecesDe).filter((n): n is NuitEspeces => n !== null)
  return { nuits, total: cents(v['total_remaining_cents']) ?? totalChamp(nuits, (n) => n.reste) }
}

function lireRemises(v: unknown): Remise[] | null {
  if (!estObjet(v) || !Array.isArray(v['deposits'])) return null
  return v['deposits'].map(remiseDe).filter((r): r is Remise => r !== null)
}

interface CompteBancaire { id: string; nom: string }

function lireComptes(v: unknown): CompteBancaire[] | null {
  if (!estObjet(v) || !Array.isArray(v['bank_accounts'])) return null
  const out: CompteBancaire[] = []
  for (const it of v['bank_accounts']) {
    if (!estObjet(it)) continue
    const id = texte(it['id'])
    const nom = texte(it['name'])
    if (id === null || nom === null || it['active'] === false) continue
    out.push({ id, nom })
  }
  return out
}

/* ═══════════════════════════ le cycle de vie ═══════════════════════════ */

function CycleRemise({ statut }: { statut: StatutRemise }) {
  const courant = rangEtape(statut)
  return (
    <div className="fr-flow">
      {ETAPES.map((e, i) => (
        <span key={e.code}>
          {i > 0 ? <span className="fr-flow__sep" aria-hidden="true">→ </span> : null}
          <span className={`fr-flow__step${i < courant ? ' fr-flow__step--done' : i === courant ? ' fr-flow__step--now' : ''}`}>
            {e.label}
          </span>
        </span>
      ))}
    </div>
  )
}

/* ═══════════════════════ créer une remise ═══════════════════════ */

function ModaleCreation(props: {
  nuits: NuitEspeces[]
  comptes: CompteBancaire[]
  onFermer: () => void
  onCree: () => void
}) {
  const toast = useToast()
  const [compte, setCompte] = useState(props.comptes[0]?.id ?? '')
  const [choisies, setChoisies] = useState<string[]>(props.nuits.map((n) => n.closingId))
  const [envoi, setEnvoi] = useState(false)

  const retenues = props.nuits.filter((n) => choisies.includes(n.closingId))
  const total = totalChamp(retenues, (n) => n.reste)
  const pret = retenues.length > 0 && !envoi

  const basculer = (id: string) => {
    setChoisies((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id])
  }

  const envoyer = async () => {
    setEnvoi(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', '/api/v1/finance/deposits',
      { bank_account_id: compte === '' ? null : compte, closing_ids: retenues.map((n) => n.closingId) },
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', `Remise créée sur ${retenues.length} nuit${retenues.length > 1 ? 's' : ''}.`)
      props.onCree()
      props.onFermer()
      return
    }
    toast('error', r.data.message ?? 'La création de la remise a été refusée.')
  }

  return (
    <Modal open onClose={props.onFermer} owning ariaLabel="Créer une remise" width={640}>
      <header className="lc-modal__head">
        <div>
          <div className="lc-modal__title">Créer une remise</div>
          <div className="fr-mini">
            Aucun montant ne se saisit ici : l’attendu est calculé par le serveur à
            partir des nuits rattachées.
          </div>
        </div>
      </header>
      <div className="lc-modal__body">
        <div className="fr-fields">
          <Field
            label="Compte bancaire"
            helper={props.comptes.length === 0
              ? 'Aucun compte actif : la remise se créera sans compte, à renseigner plus tard.'
              : 'Facultatif à ce stade — il peut être précisé au moment du dépôt.'}
          >
            <Select
              value={compte}
              options={[{ value: '', label: 'Aucun pour l’instant' }, ...props.comptes.map((c) => ({ value: c.id, label: c.nom }))]}
              onChange={setCompte}
              ariaLabel="Compte bancaire de destination"
            />
          </Field>
          <div>
            <span className="lc-label">Nuits incluses</span>
            <div className="fi-tablewrap fr-compact">
              <table className="fi-table">
                <thead>
                  <tr>
                    <th scope="col"><span className="u-sr">Inclure</span></th>
                    <th scope="col">Nuit</th>
                    <th scope="col" className="fi-money">Reste à déposer</th>
                  </tr>
                </thead>
                <tbody>
                  {props.nuits.map((n) => (
                    <tr key={n.closingId}>
                      <td>
                        <span className="fr-pick">
                          <Checkbox
                            checked={choisies.includes(n.closingId)}
                            onChange={() => basculer(n.closingId)}
                            ariaLabel={`Inclure la nuit du ${libelleJourLong(n.date)}`}
                          />
                        </span>
                      </td>
                      <td className="fr-cell-main">{libelleJour(n.date)}</td>
                      <td className="fi-money">{formatMoney(n.reste)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Attendu pour cette remise</td>
                    <td className="fi-money">{formatMoney(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div className="fr-actions fr-actions--end">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onFermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!pret} onClick={() => { void envoyer() }}>
            {envoi ? 'Création en cours' : 'Créer la remise'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════════════ faire avancer une remise ═══════════════════════ */

type Geste = 'prepare' | 'confirm' | 'reconcile'

const GESTES: Record<Geste, { titre: string; champ: string; verbe: string; perm: string }> = {
  prepare: {
    titre: 'Marquer préparée', champ: 'Montant réellement compté',
    verbe: 'Enregistrer la préparation', perm: 'finance.deposit.prepare',
  },
  confirm: {
    titre: 'Confirmer le dépôt', champ: 'Montant déposé (si le bordereau diffère)',
    verbe: 'Confirmer le dépôt', perm: 'finance.deposit.confirm',
  },
  reconcile: {
    titre: 'Rapprocher du relevé', champ: 'Montant crédité par la banque',
    verbe: 'Rapprocher', perm: 'finance.deposit.reconcile',
  },
}

function ModaleGeste(props: { remise: Remise; geste: Geste; onFermer: () => void; onFait: () => void }) {
  const toast = useToast()
  const confirmer = useConfirm()
  const [montant, setMontant] = useState('')
  const [date, setDate] = useState(ymdAujourdhui())
  const [reference, setReference] = useState('')
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const saisi = parseMoney(montant)
  // L'écart se lit toujours CONTRE L'ATTENDU : c'est lui qui vient des nuits
  // retenues, donc la seule référence qui ne dépende d'aucune saisie humaine.
  const ecart = saisi !== null && props.remise.attendu !== null ? saisi - props.remise.attendu : null
  const montantRequis = props.geste !== 'confirm'
  const motifRequis = props.geste === 'reconcile' && ecart !== null && ecart !== 0
  const pret = (!montantRequis || saisi !== null) && !envoi && (!motifRequis || motifValide(motif))

  const envoyer = async () => {
    if (montantRequis && saisi === null) return
    if (motifRequis) {
      const ok = await confirmer({
        title: 'Accepter cet écart ?',
        message: `Un écart de ${formatMoney(ecart)} sera arrêté définitivement : accepter, c’est décider que la somme manquante ne sera pas retrouvée. Votre motif et votre nom restent attachés à la remise.`,
        details: [
          { label: 'Attendu', value: formatMoney(props.remise.attendu) },
          { label: 'Crédité', value: formatMoney(saisi) },
        ],
        variant: 'sensitive',
        confirmLabel: 'Accepter l’écart',
      })
      if (!ok) return
    }
    setEnvoi(true)
    const corps: Record<string, unknown> =
      props.geste === 'prepare' ? { prepared_cents: saisi }
        : props.geste === 'confirm' ? {
          deposit_date: date,
          ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
          ...(saisi !== null ? { deposited_cents: saisi } : {}),
        }
          : { deposited_cents: saisi, ...(motif.trim() !== '' ? { reason: motif.trim() } : {}) }
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/deposits/${props.remise.id}/${props.geste}`, corps,
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', `${GESTES[props.geste].titre} : enregistré.`)
      props.onFait()
      props.onFermer()
      return
    }
    toast('error', r.data.message ?? 'Le serveur a refusé cette étape.')
  }

  return (
    <Modal open onClose={props.onFermer} owning ariaLabel={GESTES[props.geste].titre} width={560}>
      <header className="lc-modal__head">
        <div>
          <div className="lc-modal__title">{GESTES[props.geste].titre}</div>
          <div className="fr-mini">
            Attendu : {formatMoney(props.remise.attendu)} sur {props.remise.nuits.length} nuit
            {props.remise.nuits.length > 1 ? 's' : ''}.
          </div>
        </div>
      </header>
      <div className="lc-modal__body">
        <div className="fr-fields">
          {props.geste === 'confirm' ? (
            <>
              <Field label="Date de dépôt">
                <DatePicker value={date} onPick={setDate} label={libelleJour(date)} ariaLabel="Date de dépôt en banque" />
              </Field>
              <Field label="Référence du bordereau" helper="Facultatif — ce qui permettra de retrouver l’opération sur le relevé.">
                <TextInput value={reference} onChange={setReference} placeholder="Numéro du bordereau" ariaLabel="Référence du bordereau" />
              </Field>
            </>
          ) : null}
          <Field
            label={GESTES[props.geste].champ}
            helper={props.geste === 'confirm'
              ? 'Facultatif : sans montant, c’est le préparé qui fait foi — une ressaisie est une occasion de se tromper.'
              : 'Un montant en euros, tel qu’il a été compté ou lu.'}
          >
            <TextInput value={montant} onChange={setMontant} placeholder="1 240,50" autoFocus ariaLabel={GESTES[props.geste].champ} />
          </Field>
          {ecart !== null ? (
            <div className="fr-fact">
              <span className="fr-fact__label">Écart avec l’attendu</span>
              <span className="fr-fact__value"><Ecart cents={ecart} /></span>
            </div>
          ) : null}
          {motifRequis ? (
            <MotifChamp
              valeur={motif} onChange={setMotif} label="Motif de l’écart"
              aide="Douze caractères utiles au minimum — la base impose la même règle, et c’est le cœur qui tranche."
            />
          ) : null}
        </div>
        <div className="fr-actions fr-actions--end">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onFermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!pret} onClick={() => { void envoyer() }}>
            {envoi ? 'Enregistrement en cours' : GESTES[props.geste].verbe}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════════════════ détail d'une remise ═══════════════════════ */

function DetailRemise(props: {
  remise: Remise
  droits: string[]
  onRetour: () => void
  onGeste: (g: Geste) => void
}) {
  const { remise } = props
  const suivant: Geste | null =
    remise.statut === 'A_PREPARER' ? 'prepare'
      : remise.statut === 'PREPARE' ? 'confirm'
        : remise.statut === 'DEPOSE' ? 'reconcile'
          : null

  return (
    <section className="fr-section">
      <div className="fr-section__head">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onRetour}>
          <ArrowLeft size={15} aria-hidden="true" /> Toutes les remises
        </button>
        {suivant !== null && props.droits.includes(GESTES[suivant].perm) ? (
          <button type="button" className="lc-btn lc-btn--primary" onClick={() => props.onGeste(suivant)}>
            {GESTES[suivant].titre}
          </button>
        ) : null}
      </div>

      <div className="fr-kpis">
        <div className="fr-kpi fr-kpi--accent">
          <span className="fi-kpi__label">Attendu</span>
          <span className="fi-kpi__value">{formatMoney(remise.attendu)}</span>
          <span className="fr-kpi__foot">Somme des nuits rattachées, calculée par le serveur.</span>
        </div>
        <div className="fr-kpi">
          <span className="fi-kpi__label">Préparé</span>
          <span className="fi-kpi__value">{formatMoney(remise.prepare)}</span>
          <span className="fr-kpi__foot">Ce qui a été compté et mis dans la sacoche.</span>
        </div>
        <div className="fr-kpi">
          <span className="fi-kpi__label">Déposé</span>
          <span className="fi-kpi__value">{formatMoney(remise.depose)}</span>
          <span className="fr-kpi__foot">
            Écart : <Ecart cents={remise.ecart} />
            {remise.motifEcart !== null ? <> · {remise.motifEcart}</> : null}
          </span>
        </div>
      </div>

      <div className="fr-workbench">
        <div>
          <div className="fr-section__head">
            <h2 className="fr-section__title">Nuits incluses</h2>
          </div>
          {remise.nuits.length === 0 ? (
            <p className="fr-mini">
              Aucune nuit n’est encore rattachée à cette remise. L’attendu reste donc
              celui qu’a calculé le serveur : rien n’est deviné ici.
            </p>
          ) : (
            <div className="fi-tablewrap fr-compact">
              <table className="fi-table">
                <thead>
                  <tr>
                    <th scope="col">Nuit</th>
                    <th scope="col" className="fi-money">Espèces</th>
                    <th scope="col">Clôture</th>
                  </tr>
                </thead>
                <tbody>
                  {remise.nuits.map((n) => (
                    <tr key={`${n.date}-${n.closingId ?? ''}`}>
                      <td className="fr-cell-main">{libelleJour(n.date)}</td>
                      <td className="fi-money">{formatMoney(n.cents)}</td>
                      <td>
                        {n.closingId !== null
                          ? <Link className="lc-link" href={`/comptabilite/clotures/${n.closingId}`}>Ouvrir</Link>
                          : <span className="fr-mini">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total des nuits incluses</td>
                    <td className="fi-money">{formatMoney(totalChamp(remise.nuits, (n) => n.cents))}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <aside className="fr-editor">
          <div className="fr-editor__head">
            <div>
              <div className="fr-editor__title">{remise.reference ?? 'Remise sans référence'}</div>
              <div className="fr-editor__sub">{remise.compte ?? 'Compte bancaire non précisé'}</div>
            </div>
          </div>
          <CycleRemise statut={remise.statut} />
          <div className="fr-facts">
            <div className="fr-fact">
              <span className="fr-fact__label">Date de dépôt</span>
              <span className="fr-fact__value">{remise.dateDepot !== null ? libelleJour(remise.dateDepot.slice(0, 10)) : '—'}</span>
            </div>
            {remise.tracabilite.map((t) => (
              <div key={t.label} className="fr-fact">
                <span className="fr-fact__label">{t.label}</span>
                <span className="fr-fact__value">{t.valeur}</span>
              </div>
            ))}
            {remise.notes !== null ? (
              <div className="fr-fact">
                <span className="fr-fact__label">Notes</span>
                <span className="fr-fact__value">{remise.notes}</span>
              </div>
            ) : null}
          </div>
          <p className="fr-mini">
            Gestion ne se connecte à aucune banque : chaque étape ci-dessus est une
            saisie humaine, confrontée à un relevé lu ailleurs.
          </p>
        </aside>
      </div>
    </section>
  )
}

/* ═══════════════════════════════ la page ═══════════════════════════════ */

type Filtre = 'TOUTES' | StatutRemise

/**
 * LES DEUX MOITIÉS DE LA PAGE, ET LA PAGE ENTIÈRE (sous-familles du menu,
 * 07/10/2026).
 *
 * La barre latérale ouvre « Espèces à déposer » et « Remises » sous « Banque &
 * dépôts » : ce sont des VUES de cet écran, pas des pages — même chemin, même
 * Retour, `?vue=` dans l'adresse. Chacune prend alors toute la largeur ; la
 * vue d'ensemble les pose côte à côte quand la fenêtre le permet, empilées
 * sinon. Les synthèses du haut restent dans toutes les vues : on ne perd
 * jamais de vue le reste à déposer.
 *
 * Les valeurs suivent la convention des autres listes (`a_clore`,
 * `a_verifier`) ; la vue d'ensemble n'écrit rien, l'adresse nue la désigne.
 */
type VueBanque = 'ensemble' | 'a_deposer' | 'remises'

const VUES_BANQUE: { value: VueBanque; label: string }[] = [
  { value: 'ensemble', label: 'Vue d’ensemble' },
  { value: 'a_deposer', label: 'Espèces à déposer' },
  { value: 'remises', label: 'Remises' },
]

/**
 * LE FILTRE ET LA REMISE OUVERTE VIVENT DANS L'ADRESSE (`?etat=DEPOSE`,
 * `?remise=<id>`). Ouvrir une remise puis une clôture de cette remise, puis
 * revenir : en mémoire seulement, on retombait sur la liste, la remise
 * refermée. Ni l'un ni l'autre n'est une page — l'adresse est REMPLACÉE,
 * jamais empilée, et le Retour quitte la Banque en un clic.
 *
 * L'état n'est lu qu'au montage : la route remonte la page à chaque
 * navigation (`App.tsx`), un clic du menu vers une autre vue y compris.
 */
function etatInitial(): { vue: VueBanque; filtre: Filtre; remise: string | null } {
  const q = new URLSearchParams(window.location.search)
  const v = q.get('vue')
  const f = q.get('etat')
  const r = q.get('remise')
  return {
    vue: v === 'a_deposer' || v === 'remises' ? v : 'ensemble',
    filtre: f !== null && ETAPES.some((e) => e.code === f) ? (f as StatutRemise) : 'TOUTES',
    remise: r !== null && /^[0-9a-z-]{8,64}$/i.test(r) ? r : null,
  }
}

export function FinanceBankPage() {
  const { permissions, loading } = usePermissions()
  const initial = useMemo(etatInitial, [])
  const [vue, setVue] = useState<VueBanque>(initial.vue)
  const [filtre, setFiltre] = useState<Filtre>(initial.filtre)
  const [ouverte, setOuverte] = useState<string | null>(initial.remise)
  const [creation, setCreation] = useState(false)
  const [geste, setGeste] = useState<Geste | null>(null)

  const attente = useRessource('/api/v1/finance/deposits/cash-to-deposit', lireEnAttente)
  const remisesLues = useRessource('/api/v1/finance/deposits', lireRemises)
  const comptes = useRessource('/api/v1/finance/references/bank-accounts', lireComptes)

  const enAttente = attente.etat.phase === 'ok' ? attente.etat.data : null
  const toutes = remisesLues.etat.phase === 'ok' ? remisesLues.etat.data : []
  const listeComptes = comptes.etat.phase === 'ok' ? comptes.etat.data : []
  const peutCreer = !loading && permissions.includes('finance.deposit.prepare')

  const remises = useMemo(
    () => toutes.filter((r) => filtre === 'TOUTES' || r.statut === filtre),
    [toutes, filtre],
  )
  const remiseOuverte = toutes.find((r) => r.id === ouverte) ?? null
  const enCours = toutes.filter((r) => r.statut !== 'RAPPROCHE').length
  // Si aucune remise ne transmet ses nuits, la colonne disparaît plutôt que
  // d'aligner des tirets sur toute la hauteur.
  const colonneNuits = remises.some((r) => r.nuits.length > 0)
  const colonneCompte = remises.some((r) => r.compte !== null)

  const rafraichir = () => { attente.recharger(); remisesLues.recharger() }

  useEffect(() => {
    const q = new URLSearchParams()
    // La vue d'abord : c'est elle que le menu lit pour allumer sa ligne.
    if (vue !== 'ensemble') q.set('vue', vue)
    if (filtre !== 'TOUTES') q.set('etat', filtre)
    if (ouverte !== null) q.set('remise', ouverte)
    remplacerRecherche('/comptabilite/banque', q)
  }, [vue, filtre, ouverte])

  const voirEspeces = vue !== 'remises'
  const voirRemises = vue !== 'a_deposer'

  // Une remise que l'adresse nomme mais que la liste ne contient pas (lien
  // ancien, remise d'un autre exercice) : la liste s'affiche, et l'adresse
  // cesse de prétendre qu'une remise est ouverte.
  useEffect(() => {
    if (remisesLues.etat.phase === 'ok' && ouverte !== null && remiseOuverte === null) setOuverte(null)
  }, [remisesLues.etat.phase, ouverte, remiseOuverte])

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title"><Landmark size={26} aria-hidden="true" /> Banque & dépôts</h1>
          <p className="lc-subtitle">
            Les espèces en attente, les remises en route, et la question qui compte :
            est-ce que le compte y est ?
          </p>
        </div>
      </header>

      <EtatBloc etat={remisesLues.etat} quoi="les remises" />

      {remisesLues.etat.phase === 'ok' ? (
        <>
          <div className="fr-kpis">
            <div className="fr-kpi fr-kpi--accent">
              <span className="fi-kpi__label">Espèces à déposer</span>
              <span className="fi-kpi__value">{formatMoney(enAttente?.total ?? null)}</span>
              <span className="fr-kpi__foot">
                {enAttente === null
                  ? 'Les nuits en attente n’ont pas pu être lues.'
                  : `${enAttente.nuits.length} nuit${enAttente.nuits.length > 1 ? 's' : ''} finalisée${enAttente.nuits.length > 1 ? 's' : ''} dont il reste des espèces.`}
              </span>
            </div>
            <div className="fr-kpi">
              <span className="fi-kpi__label">Remises en cours</span>
              <span className="fi-kpi__value">{enCours}</span>
              <span className="fr-kpi__foot">Non encore rapprochées d’un relevé.</span>
            </div>
          </div>

          {remiseOuverte !== null ? (
            <DetailRemise
              remise={remiseOuverte}
              droits={permissions}
              onRetour={() => setOuverte(null)}
              onGeste={setGeste}
            />
          ) : (
            <>
              <div className="fr-toolbar fbk-vues">
                <SegmentedControl<VueBanque>
                  value={vue} options={VUES_BANQUE} onChange={setVue}
                  ariaLabel="Choisir ce que la page montre"
                />
              </div>

              <div className={vue === 'ensemble' ? 'fbk-duo' : 'fbk-seule'}>
                {voirEspeces ? (
                  <section className="fr-section">
                    <div className="fr-section__head">
                      <h2 className="fr-section__title">Espèces à déposer</h2>
                      {peutCreer && enAttente !== null && enAttente.nuits.length > 0 ? (
                        <button type="button" className="lc-btn lc-btn--primary" onClick={() => setCreation(true)}>
                          <Plus size={15} aria-hidden="true" /> Créer une remise
                        </button>
                      ) : null}
                    </div>
                    <EtatBloc etat={attente.etat} quoi="les espèces en attente" />
                    {enAttente !== null ? (
                      enAttente.nuits.length === 0 ? (
                        <div className="fi-empty">
                          <div className="fi-empty__title">Rien en attente</div>
                          Toutes les espèces reconnues sont rattachées à une remise.
                        </div>
                      ) : (
                        <div className="fi-tablewrap">
                          <table className="fi-table">
                            <thead>
                              <tr>
                                <th scope="col">Nuit</th>
                                <th scope="col" className="fi-money">Compté</th>
                                <th scope="col" className="fi-money">Reconnu</th>
                                <th scope="col" className="fi-money">Reste à déposer</th>
                                <th scope="col">Clôture</th>
                              </tr>
                            </thead>
                            <tbody>
                              {enAttente.nuits.map((n) => (
                                <tr key={n.closingId}>
                                  <td className="fr-cell-main">
                                    {libelleJour(n.date)}
                                    {n.revision !== null ? <small className="fr-cell-sub">Révision {n.revision}</small> : null}
                                  </td>
                                  <td className="fi-money fi-money--faible">{formatMoney(n.compte)}</td>
                                  <td className="fi-money fi-money--faible">{formatMoney(n.reconnu)}</td>
                                  <td className="fi-money fi-money--fort">{formatMoney(n.reste)}</td>
                                  <td>
                                    <Link className="lc-link" href={`/comptabilite/clotures/${n.closingId}`}>Ouvrir</Link>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={3}>Total restant à déposer</td>
                                <td className="fi-money">{formatMoney(enAttente.total)}</td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )
                    ) : null}
                    <p className="fr-mini">
                      « Compté » est l’argent physique mesuré, « reconnu » la recette espèces
                      arrêtée par la clôture : ils peuvent légitimement différer (fonds de
                      caisse, appoint). Seul le reste à déposer commande une remise.
                    </p>
                  </section>
                ) : null}

                {voirRemises ? (
                  <section className="fr-section">
                    <div className="fr-section__head">
                      <h2 className="fr-section__title">Remises</h2>
                      <SegmentedControl
                        value={filtre} size="sm" ariaLabel="Filtrer les remises"
                        options={[{ value: 'TOUTES' as Filtre, label: 'Toutes' }, ...ETAPES.map((e) => ({ value: e.code as Filtre, label: e.label }))]}
                        onChange={setFiltre}
                      />
                    </div>
                    {remises.length === 0 ? (
                      <div className="fi-empty">
                        <div className="fi-empty__title">Aucune remise dans cet état</div>
                        Changez de filtre, ou créez une remise depuis les espèces en attente.
                        {/* Seule, la vue des remises ne montre pas ces espèces : un
                            geste y mène, plutôt qu'une consigne qu'on ne peut pas suivre. */}
                        {vue === 'remises' ? (
                          <div className="fbk-vide__geste">
                            <button type="button" className="lc-btn lc-btn--sm" onClick={() => setVue('a_deposer')}>
                              <Banknote size={14} aria-hidden="true" /> Voir les espèces à déposer
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="fi-tablewrap">
                        <table className="fi-table">
                          <thead>
                            <tr>
                              <th scope="col">Remise</th>
                              {colonneCompte ? <th scope="col">Compte</th> : null}
                              {colonneNuits ? <th scope="col">Nuits</th> : null}
                              <th scope="col" className="fi-money">Attendu</th>
                              <th scope="col" className="fi-money">Déposé</th>
                              <th scope="col" className="fi-money">Écart</th>
                              <th scope="col">État</th>
                            </tr>
                          </thead>
                          <tbody>
                            {remises.map((r) => (
                              <tr
                                key={r.id} className="fr-row" tabIndex={0} role="button"
                                aria-label={`Détail de la remise ${r.reference ?? r.id}`}
                                onClick={() => setOuverte(r.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOuverte(r.id) } }}
                              >
                                <td className="fr-cell-main">
                                  <Banknote size={14} aria-hidden="true" /> {r.reference ?? 'Remise'}
                                  {r.dateDepot !== null ? <small className="fr-cell-sub">{libelleJour(r.dateDepot.slice(0, 10))}</small> : null}
                                </td>
                                {colonneCompte ? <td>{r.compte ?? <span className="fr-mini">—</span>}</td> : null}
                                {colonneNuits ? <td>{r.nuits.length > 0 ? r.nuits.length : <span className="fr-mini">—</span>}</td> : null}
                                <td className="fi-money">{formatMoney(r.attendu)}</td>
                                <td className="fi-money">{formatMoney(r.depose)}</td>
                                <td className="fi-money"><Ecart cents={r.ecart} /></td>
                                <td>
                                  <span className={`lc-pill${r.statut === 'RAPPROCHE' ? ' lc-pill--ok' : r.statut === 'A_PREPARER' ? ' lc-pill--idle' : ''}`}>
                                    {libelleEtape(r.statut)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                ) : null}
              </div>
            </>
          )}
        </>
      ) : null}

      {creation ? (
        <ModaleCreation
          nuits={enAttente?.nuits ?? []}
          comptes={listeComptes}
          onFermer={() => setCreation(false)}
          onCree={rafraichir}
        />
      ) : null}

      {geste !== null && remiseOuverte !== null ? (
        <ModaleGeste
          remise={remiseOuverte} geste={geste}
          onFermer={() => setGeste(null)}
          onFait={rafraichir}
        />
      ) : null}
    </main>
  )
}
